import React, { useCallback, useEffect, useState } from 'react';
import { Button, CircularProgress, Grid, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { renderDropdownNonSimple } from 'components/form/field/redux-field';
import { StringOption } from 'interfaces/forms/types';
import { UserPermissions } from 'interfaces/user/Permissions';
import CustomerDropdown from '../components/customer-dropdown';
import { SectionTitleStyled } from '../components/section-title';
import {
  FILE_FREQUENCY_LABELS,
  FILE_TYPE_LABELS,
  FileFrequencyEnum,
  FileTypeEnum,
  IFileImportFileTracker,
} from './types';
import { getFiles } from './file-import-service';
import FileImportListTable from './file-import-list-table';

const FORM_NAME = 'fileImportList';

interface IFilterFormValues {
  fileType: StringOption | null;
  frequency: StringOption | null;
}

const FILE_TYPE_OPTIONS: StringOption[] = [
  { label: 'All', value: '' },
  ...Object.values(FileTypeEnum).map(value => ({ label: FILE_TYPE_LABELS[value], value })),
];

const FREQUENCY_OPTIONS: StringOption[] = [
  { label: 'All', value: '' },
  ...Object.values(FileFrequencyEnum).map(value => ({
    label: FILE_FREQUENCY_LABELS[value],
    value,
  })),
];

const FileImportList = (props: InjectedFormProps<IFilterFormValues>): JSX.Element => {
  const { handleSubmit } = props;

  const [selectedCustomerId, setSelectedCustomerId] = useState<number>(0);
  const [files, setFiles] = useState<IFileImportFileTracker[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchFiles = useCallback(
    async (customerId: number, fileType?: string, frequency?: string) => {
      if (!customerId) {
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const result = await getFiles(customerId, fileType || undefined, frequency || undefined);
        setFiles(result);
      } catch {
        setLoadError('Failed to load files. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    setFiles([]);
    fetchFiles(selectedCustomerId);
  }, [selectedCustomerId, fetchFiles]);

  const handleCustomerChange = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setFiles([]);
    setLoadError(null);
  };

  const onSubmit = (values: IFilterFormValues) => {
    fetchFiles(selectedCustomerId, values.fileType?.value, values.frequency?.value);
  };

  const handleRefresh = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <>
      <SectionTitleStyled
        title="File Import List"
        rightSideItems={
          selectedCustomerId
            ? [
                {
                  key: 'refresh',
                  element: (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      disabled={loading}
                    >
                      Refresh
                    </Button>
                  ),
                },
              ]
            : []
        }
      />
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Grid container direction="column" spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={3}>
                <CustomerDropdown
                  isLabelOn
                  isSelectedOption
                  selectedCustomerId={selectedCustomerId}
                  setSelectedCustomerId={handleCustomerChange}
                  requiredPermission={UserPermissions.FileImportUpload}
                />
              </Grid>
              {selectedCustomerId !== 0 && (
                <>
                  <Grid item xs={2} marginBottom="-6px">
                    <Field
                      name="fileType"
                      label="File Type"
                      component={renderDropdownNonSimple}
                      fields={FILE_TYPE_OPTIONS}
                    />
                  </Grid>
                  <Grid item xs={2} marginBottom="-6px">
                    <Field
                      name="frequency"
                      label="Frequency"
                      component={renderDropdownNonSimple}
                      fields={FREQUENCY_OPTIONS}
                    />
                  </Grid>
                  <Grid item>
                    <Button type="submit" variant="contained" color="primary" disabled={loading}>
                      Apply Filters
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
          {loading && (
            <Grid item>
              <CircularProgress />
            </Grid>
          )}
          {!loading && loadError && (
            <Grid item>
              <Typography color="error">{loadError}</Typography>
            </Grid>
          )}
          {!loading && !loadError && selectedCustomerId === 0 && (
            <Grid item>
              <Typography color="textSecondary">
                Select a customer to view uploaded files.
              </Typography>
            </Grid>
          )}
          {!loading && !loadError && selectedCustomerId !== 0 && files.length === 0 && (
            <Grid item>
              <Typography color="textSecondary">No files found for this customer.</Typography>
            </Grid>
          )}
          {!loading && !loadError && files.length > 0 && (
            <Grid item xs={12}>
              <FileImportListTable files={files} />
            </Grid>
          )}
        </Grid>
      </form>
    </>
  );
};

export default reduxForm<IFilterFormValues>({
  form: FORM_NAME,
  initialValues: { fileType: FILE_TYPE_OPTIONS[0], frequency: FREQUENCY_OPTIONS[0] },
})(FileImportList);
